import { getBrandForPersona } from "@/data/genies/persona-brands";
import { Agent } from "@/types/agent/agent";
import { BRAND_MASCOT, BRAND_MASCOT_POOL, resolveLegacyGeniePath } from "@/data/brandMascots";

// Genie image utilities for consistent avatar selection.
//
// Migrated to the new /genie-mascots/ library. Legacy callers that pass
// /genie-images/N.png are auto-translated through resolveLegacyGeniePath
// in MascotImg, so DB rows storing old paths still render correctly.

/**
 * Resolve the best available avatar/logo URL for a genie.
 * Falls back through: agent branding → agent_pages image → agent_pages branding → local brand logo.
 * DEPRECATED: agent_pages table is obsolete and scheduled for deletion — do not add new usages.
 * Legacy /genie-images/ paths are mapped to the new library before return.
 */
export const resolveGenieAvatarUrl = (agent: Agent): string | undefined => {
  // 1. Check database sources
  const dbUrl =
    agent.branding?.logoUrl ||
    agent.branding?.customAvatarUrl ||
    agent.page?.genie_image_url ||
    agent.page?.branding?.logoUrl ||
    agent.page?.branding?.customAvatarUrl;

  if (dbUrl) return resolveLegacyGeniePath(dbUrl);

  // 2. For demo genies, fall back to local static brand logo (same as Discover page)
  if (agent.persona_id) {
    const brand = getBrandForPersona(agent.persona_id);
    if (brand) {
      const safeName = brand.brandName.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      return `/brand-logos/${safeName}-stacked.png`;
    }
  }

  return undefined;
};

// Gender mapping splits the 13-slot brand pool into rough male/female/neutral
// buckets so callers passing a `gender` hint stay stable. The buckets are by
// pool index, not by source filename, so they hold across asset swaps.
const GENDER_INDEX_MAPPING = {
  male: [0, 2, 4, 6, 8, 10, 12],
  female: [1, 3, 5, 7, 9, 11],
  neutral: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
} as const;

/**
 * Get a consistent genie image for an agent based on their ID.
 * Same agent ID always resolves to the same mascot.
 */
export const getGenieImage = (agentId: string, gender?: 'male' | 'female' | 'neutral'): string => {
  let hash = 0;
  for (let i = 0; i < agentId.length; i++) {
    const char = agentId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }

  const indexPool = gender && GENDER_INDEX_MAPPING[gender]
    ? GENDER_INDEX_MAPPING[gender]
    : GENDER_INDEX_MAPPING.neutral;

  const poolIndex = indexPool[Math.abs(hash) % indexPool.length];
  return BRAND_MASCOT_POOL[poolIndex] ?? BRAND_MASCOT;
};

/**
 * Get a random genie image (for when consistency isn't needed)
 */
export const getRandomGenieImage = (_gender?: 'male' | 'female' | 'neutral'): string => {
  const index = Math.floor(Math.random() * BRAND_MASCOT_POOL.length);
  return BRAND_MASCOT_POOL[index] ?? BRAND_MASCOT;
};

/**
 * Preload the brand mascot for better perceived performance.
 */
export const preloadGenieImages = (): void => {
  const img = new Image();
  img.src = BRAND_MASCOT;
};
